import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import Layout from './Layout/Layout';
import Link from '@mui/material/Link';
import AuthDialog from '../components/dialogs/AuthDialog';
import AuthDialogConstants from '../components/dialogs/AuthDialog/AuthDialogConstants';
import { logout, checkToken } from '../utilities/authUtilities';
import { EXPIRATION_ONBOARDING_INFO_CLOSED_KEY } from '../utilities/storageUtilities';
import VideosPageConstants from '../components/pages/VideosPage/VideosPageConstants';
import { trackPage, loadAnalytics, loadAnalyticsConsent } from '../utilities/analyticsUtilities';
import AppConstants from './AppConstants';
import { appOperations } from './store';
import ColoredSnackbar from '../components/misc/ColoredSnackbar/ColoredSnackbar';
import AlertMessage from '../components/misc/AlertMessage';
import ConsentPopup from '../components/dialogs/ConsentDialog/ConsentPopup';
import { Head } from './Layout/components/Head';
import TextInformationDialog from '../components/dialogs/TextInformationDialog';
import BackgroundVideoElement from '../components/pages/CreatePage/Preview/BackgroundVideoElement';
import PremiumVideosDialog from '../components/pages/CreatePage/MediaPickers/common/PremiumVideosDialog';

const styles = (theme) => ({
    root: {
        textAlign: 'center',
    },
    consentContainer: {
        width: 300,
        position: 'fixed',
        right: 0,
        zIndex: 999999,
        top: theme.spacing(8)
    },
    upgradeLink: {
        display: 'contents',
        color: 'inherit',
        cursor: 'pointer'
    }
});

const App = (props) => {
    const [showConsentPopup, setShowConsentPopup] = useState(false);
    const { classes, snackOpen, snackMessage, snackVariant, closeSnack, loggedIn, openAuthDialog, accountDetails, loadPurchaseOptions, makeLoggedIn } = props;

    useEffect(() => {
        checkToken(
            true,
            makeLoggedIn,
            () => openAuthDialog(AuthDialogConstants.FORM_TYPE_LOGIN)
        );
    }, []);

    useEffect(() => {
        // Track page if it's a known route
        if (AppConstants.ROUTES.includes(window.location.pathname.split('/')[1])) {
            trackPage(window.location.pathname);
        }

        // Set consent popup state
        setShowConsentPopup(loadAnalyticsConsent());

        // Load analytics and purchase options
        loadAnalytics();
        loadPurchaseOptions();
    }, [loadPurchaseOptions]);

    return (
        <div className={classes.root}>
            <Layout
                logInPress={() => openAuthDialog(AuthDialogConstants.FORM_TYPE_LOGIN)}
                logOutPress={logout}
                loggedIn={loggedIn}
                accountDetails={accountDetails}
            />
            <Head />
            <AuthDialog />
            <ColoredSnackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={snackOpen}
                autoHideDuration={6000}
                onClose={closeSnack}
                message={snackMessage}
                variant={snackVariant}
            />

            {/* It shouldn't appear to people who have upgraded */}
            {/* The AlertMessage should NOT be visible to users that are not logged in. */}
            {/* Newly created users on the same PC should not see the message. */}
            {(Boolean(accountDetails.email) && accountDetails.subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE) &&
                <AlertMessage
                    severity='error'
                    variant='filled'
                    storageKey={EXPIRATION_ONBOARDING_INFO_CLOSED_KEY}
                    showByDate={new Date('2021-04-03T00:00:00')}
                >
                    Policy Update: Old projects will be deleted {VideosPageConstants.FREE_VIDEO_DAYS_LIMIT} days after creation.
                    Make sure you download any exports you want to keep. &nbsp;
                    <Link to='/plans' className={classes.upgradeLink}>Upgrade</Link>
                    &nbsp; for unlimited storage and to keep your projects from expiring.
                </AlertMessage>
            }

            {showConsentPopup && <ConsentPopup />}

            <TextInformationDialog />

            {/* Render the hidden video element for background preview */}
            <BackgroundVideoElement />

            {/* Render the acquisition dialog for pro videos */}
            <PremiumVideosDialog />
        </div>
    );
}

const mapStateToProps = (state) => {
    const {
        snackOpen,
        snackMessage,
        snackVariant,
        loggedIn,
        accountDetails
    } = state.app;

    const { id: videoSettingsId } = state.createPreview.present.videoSettings;

    return {
        snackOpen,
        snackMessage,
        snackVariant,
        loggedIn,
        videoSettingsId,
        accountDetails,
    }
}

const mapDispatchToProps = (dispatch) => {
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));
    const closeSnack = () => dispatch(appOperations.closeSnack());
    const openAuthDialog = (formType) => dispatch(appOperations.openAuthDialog(formType));
    const loadPurchaseOptions = () => dispatch(appOperations.loadPurchaseOptions());
    const makeLoggedIn = (token) => { dispatch(appOperations.makeLoggedIn(token)) };

    return {
        openSnack,
        closeSnack,
        openAuthDialog,
        loadPurchaseOptions,
        makeLoggedIn,
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(App, styles));